import { useToastStore } from '@/store';
import type { ToastMessage, ToastProps } from 'primereact/toast';
import NotificationToast from '../components/NotificationToast';
import type { ToastAction } from './notification.types';

/**
 * Displays a toast notification with the specified message, severity, and options.
 * @param {string} message - The message to display in the toast.
 * @param {ToastMessage['severity']} severity - The severity level the message ("success" | "secondary" | "info" | "contrast" | "warn" | "error").
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const show = (
	message: string,
	severity: ToastMessage['severity'],
	toastMessageOpts: Partial<ToastMessage> = {},
	toastProps: Partial<ToastProps> = {},
) => {
	const { toastRef, setToastProps } = useToastStore.getState();

	// Separate pt (passthrough props) from other toast props
	// pt should not be persisted globally, so we handle it separately
	const { pt, ...otherToastProps } = toastProps;

	// Only persist non-pt props globally
	if (Object.keys(otherToastProps).length > 0) {
		setToastProps(otherToastProps);
	}

	if (toastRef?.current) {
		toastRef.current.show({
			severity,
			summary: severity,
			detail: message,
			...toastMessageOpts,
			// Apply pt directly to this specific toast message
			...(pt ? { pt } : {}),
		});
	}
};

/**
 * Displays a success toast notification with the specified message, and options.
 * @param {string} message - The message to display in the toast.
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const showSuccess = (
	message: string,
	toastMessageOpts: Partial<ToastMessage> = {},
	toastProps: Partial<ToastProps> = {},
) => {
	show(message, 'success', toastMessageOpts, toastProps);
};

/**
 * Displays a warning toast notification with the specified message, and options.
 * @param {string} message - The message to display in the toast.
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const showWarning = (
	message: string,
	toastMessageOpts: Partial<ToastMessage> = {},
	toastProps: Partial<ToastProps> = {},
) => {
	show(message, 'warn', toastMessageOpts, toastProps);
};

/**
 * Displays an info toast notification with the specified message, and options.
 * @param {string} message - The message to display in the toast.
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const showInfo = (
	message: string,
	toastMessageOpts: Partial<ToastMessage> = {},
	toastProps: Partial<ToastProps> = {},
) => {
	show(message, 'info', toastMessageOpts, toastProps);
};

/**
 * Displays an error toast notification with the specified message, and options.
 * @param {string} message - The message to display in the toast.
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const showError = (
	message: string,
	toastMessageOpts: Partial<ToastMessage> = {},
	toastProps: Partial<ToastProps> = {},
) => {
	show(message, 'error', toastMessageOpts, toastProps);
};

/**
 * Displays an error toast notification with the specified message, and options.
 * @param {string} message - The message to display in the toast.
 * @param {Partial<ToastMessage>} [toastMessageOpts={}] - Optional additional options for the message, e.g. `summary`, `life`.
 * @param {ToastAction} [action] - Appends a clickable action message at the end of the toast.
 * @param {Partial<ToastProps>} [toastProps={}] - Optional props to pass to the `Toast` component, e.g. `position`.
 */
export const showNotification = (
	message: string,
	toastMessageOpts: Partial<ToastMessage> = {},
	action?: ToastAction,
	toastProps: Partial<ToastProps> = {},
) => {
	const defaultOpts: Partial<ToastMessage> = {
		closable: false,
		content: ({ message }) => (
			<NotificationToast message={message.detail} action={action} />
		),
	};

	show(
		message,
		'contrast',
		{
			...defaultOpts,
			...toastMessageOpts,
		},
		toastProps,
	);
};
